import React, { useMemo } from "react";
import {
  useOutletContext,
  useSearchParams,
  useNavigate,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import NavbarScroll from "../../components/subComponents/NavbarScroll";
import { useItemCategoryInfinite } from "../../hooks/useItemCategoryInfinite";
import { ConnectCard1 } from "../../components/subComponents/ConnectCard1";
import { saffronRout, saffronURL } from "../../Url";

const safeArray = (v) => (Array.isArray(v) ? v : []);
const addPosition = (arr) =>
  safeArray(arr).map((item, i) => ({ ...item, position: i }));

// ✅ keep filters + category_name, and set section for prev/next
const keepKeys = ["country", "state", "city", "language", "category", "category_name"];

const buildSafeSearch = (searchParams) => {
  const current = new URLSearchParams(searchParams);
  const next = new URLSearchParams();

  keepKeys.forEach((k) => {
    const v = current.get(k);
    if (v) next.set(k, v);
  });

  // ✅ details page will use this to build prev/next list
  next.set("section", "ebooks");

  return `?${next.toString()}`;
};

const Ebooks = () => {
  const {
    selectedCountry,
    selectedCategory,
    selectedLanguage,
    selectedState,
    selectedCity,
  } = useOutletContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const categoryName = searchParams.get("category_name") || "";

  const {
    item: ebooks,
    ref,
    hasNextPage,
    isFetchingNextPage,
    isPending: ebooksPending,
  } = useItemCategoryInfinite({
    key: "connect_ebooks",
    endpoint: "ebooks",
    listKey: "ebooks",
    category_name: categoryName,
    selectedCategory,
    selectedCountry,
    selectedLanguage,
    selectedState,
    selectedCity,
    enabled: true,
    threshold: 0.7,
  });

  const hasEbooks = (ebooks?.length || 0) > 0;

  /** ✅ Menu data */
  const { data: menuData, isLoading: loadingTree } = useQuery({
    queryKey: [
      "menuData_connect_slide",
      selectedCountry,
      selectedLanguage,
      selectedCategory,
      selectedState,
      selectedCity,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCountry) params.append("country", selectedCountry);
      if (selectedLanguage) params.append("language", selectedLanguage);
      if (selectedCategory) params.append("category", selectedCategory);
      if (selectedState) params.append("state", selectedState);
      if (selectedCity) params.append("city", selectedCity);

      const url = `${saffronURL}/api/v1/public/menu?${params.toString()}`;
      const resp = await axios.get(url);
      return resp.data;
    },
    enabled: !!(selectedCountry || selectedLanguage || selectedCategory),
    retry: 2,
    staleTime: 1000 * 60 * 10,
    cacheTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
  });

  // ✅ FIX: ebooks menu key should be "ebook" or "ebooks" as per your menu
  // (Most projects use "ebook" — adjust if your menu uses "ebooks")
  const navbaritems = useMemo(() => {
    return (
      menuData?.data?.items?.find((item) => item.key === "ebook") ||
      menuData?.data?.items?.find((item) => item.key === "ebooks")
    );
  }, [menuData]);

  const getEbooks = useMemo(() => addPosition(ebooks), [ebooks]);

  /** ✅ Navbar category select */
  const handleCategorySelect = (child) => {
    const params = new URLSearchParams(searchParams);

    if (child?.code) params.set("category_name", child.code);
    else params.delete("category_name");

    setSearchParams(params, { replace: false });
    window.scrollTo(0, 0);
  };

  /** ✅ CARD CLICK => navigate to details route */
  const handleOpenDetails = (item, idx) => {
    const slug = item?.slug || item?.post_slug || item?.id;
    if (!slug) return;

    navigate(
      {
        // ✅ IMPORTANT: type must match getSingleApi() case in ConnectDetailsPage
        // If your getSingleApi uses "ebooks" (plural), keep "ebooks"
        pathname: `${saffronRout}/details/ebooks/${slug}`,
        search: buildSafeSearch(searchParams),
      },
      {
        state: {
          sectionKey: "ebooks",
          position: idx ?? item?.position ?? 0,
        },
      }
    );

    window.scrollTo(0, 0);
  };

  return (
    <>
      <NavbarScroll
        items={navbaritems}
        activeCode={categoryName}
        onSelect={handleCategorySelect}
        loading={loadingTree}
      />

      <section className="py-3 px-1 md:px-5 lg:px-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {getEbooks?.map((item, idx) => (
            <ConnectCard1
              key={item.id}
              item={item}
              onArticleClick={() => handleOpenDetails(item, idx)}
               imgH=" h-[260px]"
              objimg="object-fill"
            />
          ))}
        </div>

        {ebooksPending && <p className="text-gray-500">Loading ebooks…</p>}

        {!ebooksPending && !hasEbooks && (
          <p className="text-gray-400 italic">
            No items found in <span className="font-medium">'Ebooks'</span>
          </p>
        )}

        {hasNextPage && <div ref={ref} className="h-10" />}

        {isFetchingNextPage && (
          <div className="text-center text-sm text-gray-500 py-4">
            Loading more...
          </div>
        )}
      </section>
    </>
  );
};

export default Ebooks;